import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';

import {
    BehaviorSubject,
    Observable,
    Subject,
    Subscription,
    combineLatest,
    distinctUntilChanged,
    filter,
    map,
    of,
    shareReplay,
    switchMap,
    takeUntil,
    timer
} from 'rxjs';
import {
    EntityType,
    GetReferral,
    GetReferralDetails,
    IWorklistItemDto,
    SettingType,
    UpsertNotification,
    UrgencyType
} from '@app/api-module/control-centre-api-client';
import { OrgSiteSelectionService } from '@app/shared/org-site-selection/org-site-selection.service';
import { ReferralsService } from '@app/referrals/referrals.service';
import { Org } from '@app/models';
import { DateTime } from 'luxon';
import { WorklistService } from '@app/worklist/worklist.service';
import { v4 as uuidv4 } from 'uuid';
import { HubConnection } from '@microsoft/signalr';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageBoxButtons, MessageBoxIcon } from '@app/messagebox/messagebox.model';
import { MessageBoxService } from '@app/messagebox/services/messagebox.service';
import { ImagingRequestsFiltersComponent } from './imaging-requests-filters/imaging-requests-filters.component';
import { ImagingRequest } from './models/imaging-requests.model';
import { ImagingRequestStatus } from './models/enums';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ImagingRequestFormComponent } from './imaging-request-form/imaging-request-form.component';
import { AuthenticationService } from '@app/authentication/services/authentication.service';
import { RoleEnum } from '@app/api-module/user-management-api-client-v2';
import { SettingUtilities } from '@app/shared/setting-utilities';
import { CommonUtilities } from '@app/shared/common-utilities';

@Component({
    selector: 'app-imaging-requests',
    templateUrl: './imaging-requests.component.html',
    styleUrls: ['./imaging-requests.component.scss']
})
export class ImagingRequestsComponent implements OnInit, OnDestroy {
    private static readonly RecentOrdersHoursRange: number = 16; // for orders retrieval
    private static readonly RecentReferralsHoursRange: number = 32; // for referral retrieval
    private static readonly ImageRequestDisplayCutoffHours: number = 16; // for deciding which imaging requests to display

    private static readonly PeriodicRefreshInterval: number = 1000 * 60 * 1; // 1 minute;

    private readonly _destroy$: Subject<boolean> = new Subject<boolean>();

    private readonly _subscriptions = new Subscription();
    private _referralHubSubscriptionId: string;
    private _hub: HubConnection;

    private readonly _filteredUrgencyTypes$ = new BehaviorSubject<UrgencyType[]>([]);
    private readonly _filteredDoctors$ = new BehaviorSubject<string[]>([]);
    private readonly _searchTerm$ = new BehaviorSubject<string>('');

    private readonly _upsertedReferrals = new BehaviorSubject<Map<number, GetReferralDetails>>(new Map<number, GetReferralDetails>());
    private readonly _deletedReferrals = new BehaviorSubject<Set<number>>(new Set<number>());

    private readonly _upsertedOrders = new BehaviorSubject<Map<string, IWorklistItemDto>>(new Map<string, IWorklistItemDto>());
    private readonly _deletedOrders = new BehaviorSubject<Set<string>>(new Set<string>());

    private readonly _deletedLinkedOrderIds$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());

    @ViewChild(ImagingRequestsFiltersComponent)
    private readonly _filters: ImagingRequestsFiltersComponent;

    searchForm: FormGroup;
    hasResults: boolean = false;

    hasSearchTerm$: Observable<boolean>;
    hasFilters$: Observable<boolean>;

    imagingRequestStatus = ImagingRequestStatus;

    imagingRequestsByStatus: Map<ImagingRequestStatus, BehaviorSubject<ImagingRequest[]>>;

    referringDoctors$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    showReferralColumns$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    canCreateNewVettingRequests$: Observable<boolean>;
    userCanEditImagingRequests$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    constructor(
        private readonly authenticationService: AuthenticationService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly referralsService: ReferralsService,
        private readonly worklistService: WorklistService,
        private readonly messageBoxService: MessageBoxService,
        private readonly dialog: MatDialog,
        formBuilder: FormBuilder
    ) {
        const imagingRequestStatuses = Object.keys(ImagingRequestStatus) as Array<keyof typeof ImagingRequestStatus>;
        this.imagingRequestsByStatus = new Map(imagingRequestStatuses.map(status => [status, new BehaviorSubject<ImagingRequest[]>([])]));
        this.searchForm = formBuilder.group({
            searchTerm: new FormControl('', [Validators.minLength(3)])
        });

        this.hasSearchTerm$ = this._searchTerm$.pipe(map(searchTerm => !!searchTerm));
        this.hasFilters$ = combineLatest([this.hasSearchTerm$, this._filteredUrgencyTypes$, this._filteredDoctors$]).pipe(
            map(
                ([hasSearchTerm, filteredUrgencyTypes, filteredDoctors]) =>
                    hasSearchTerm || filteredUrgencyTypes.length > 0 || filteredDoctors.length > 0
            )
        );
    }

    ngOnInit(): void {
        const selectedOrg = this.orgSiteSelectionService.getOrgSelected$();
        const allowedSites = this.orgSiteSelectionService
            .getAllowedSites()
            .pipe(
                distinctUntilChanged(([previousAllowedSites, _oldDefaultSite], [currentAllowedSites, _currentDefaultSite]) =>
                    CommonUtilities.arraysEqual(previousAllowedSites, currentAllowedSites)
                )
            );
        const selectedSiteId = combineLatest([this.orgSiteSelectionService.getSitesSelected(), allowedSites]).pipe(
            map(([selectedSiteIds, [allowedSites, defaultSiteId]]) => {
                if (selectedSiteIds.length === 1 && selectedSiteIds[0]) {
                    return selectedSiteIds[0];
                }

                if (defaultSiteId) {
                    return defaultSiteId;
                }

                if (allowedSites.length > 0) {
                    return allowedSites[0].siteId;
                }

                return null;
            }),
            filter(siteId => siteId !== null),
            distinctUntilChanged()
        );

        this._subscriptions.add(
            combineLatest([selectedOrg, selectedSiteId]).subscribe(async ([org, selectedSiteId]) => {
                await this.initHubSubscriptions(org.orgCode, selectedSiteId);
                this.clearUpserts();
                this.clearFiltersAndSearch();
            })
        );

        const referrals: Observable<GetReferral[]> = this.getReferrals(selectedOrg, selectedSiteId).pipe(
            switchMap(referrals => this.removeDeletedReferrals(referrals)),
            switchMap(referrals => this.upsertChangedReferrals(referrals))
        );

        const orders: Observable<IWorklistItemDto[]> = this.getOrders(selectedOrg, selectedSiteId).pipe(
            switchMap(orders => this.removeDeletedOrders(orders)),
            switchMap(orders => this.upsertChangedOrders(orders))
        );

        const deletedLinkedOrders = this._deletedLinkedOrderIds$.asObservable();
        const imagingRequests: Observable<ImagingRequest[]> = this.getImagingRequests(referrals, orders, deletedLinkedOrders).pipe(
            shareReplay({ refCount: true })
        );

        this._subscriptions.add(
            imagingRequests
                .pipe(map(requests => new Set(requests.map(request => request.referringDoctor).filter(doctor => !!doctor))))
                .subscribe(referringDoctors => {
                    this.referringDoctors$.next([...referringDoctors].sort((left, right) => left.localeCompare(right)));
                })
        );

        const filteredImagingRequests = combineLatest([
            imagingRequests,
            this._filteredUrgencyTypes$,
            this._filteredDoctors$,
            this._searchTerm$
        ]).pipe(
            map(([imagingRequests, filteredUrgencies, filteredDoctors, searchTerm]) => {
                const urgencies = new Set<UrgencyType>(filteredUrgencies);
                const doctors = new Set<string>(filteredDoctors);
                return imagingRequests.filter(request => this.canShowImagingRequest(request, urgencies, doctors, searchTerm));
            })
        );

        this._subscriptions.add(
            selectedOrg.pipe(map(org => this.shouldShowReferralColumns(org))).subscribe(show => this.showReferralColumns$.next(show))
        );

        const orgCanCreateVettingRequests$ = this.orgSiteSelectionService
            .getOrgSelectedSettings$()
            .pipe(map(orgSettings => SettingUtilities.isSettingEnabled(orgSettings, SettingType.ClientReferralCreation)));

        this.canCreateNewVettingRequests$ = combineLatest([
            orgCanCreateVettingRequests$,
            selectedOrg,
            this.authenticationService.currentUser
        ]).pipe(
            map(
                ([orgCanCreateVettingRequests, org, user]) =>
                    orgCanCreateVettingRequests && user.hasRole(RoleEnum.ImagingRequestEditor, org.orgCode)
            )
        );

        this._subscriptions.add(
            combineLatest([selectedOrg, this.authenticationService.currentUser])
                .pipe(map(([org, user]) => user.hasRole(RoleEnum.ImagingRequestEditor, org.orgCode)))
                .subscribe(canEdit => {
                    this.userCanEditImagingRequests$.next(canEdit);
                })
        );
        this._subscriptions.add(this.userCanEditImagingRequests$.subscribe());

        this._subscriptions.add(
            filteredImagingRequests.subscribe(imagingRequests => {
                this.hasResults = imagingRequests?.filter(r => r.status != ImagingRequestStatus.Cancelled).length > 0;

                const imagingRequestStatuses = Object.keys(ImagingRequestStatus) as Array<keyof typeof ImagingRequestStatus>;

                const groupedImagingRequests: Map<ImagingRequestStatus, ImagingRequest[]> = new Map(
                    imagingRequestStatuses.map(status => [status, []])
                );
                imagingRequests.forEach(imagingRequest => {
                    groupedImagingRequests.get(imagingRequest.status)?.push(imagingRequest);
                });
                groupedImagingRequests.forEach((imagingRequests: ImagingRequest[], status: ImagingRequestStatus) => {
                    this.imagingRequestsByStatus.get(status)?.next(imagingRequests);
                });
            })
        );
    }

    getImagingRequestsByStatus(status: ImagingRequestStatus): Observable<ImagingRequest[]> {
        return this.imagingRequestsByStatus.get(status).asObservable();
    }

    getImagingRequests(
        referrals: Observable<GetReferral[]>,
        orders: Observable<IWorklistItemDto[]>,
        deletedLinkedOrders: Observable<Set<number>>
    ): Observable<ImagingRequest[]> {
        return combineLatest([referrals, orders, deletedLinkedOrders]).pipe(
            map(([latestReferrals, latestOrders, deletedLinkedOrders]) => {
                const ordersByAccession = Object.fromEntries(latestOrders.map(order => [order.accessionNumber, order]));
                const ordersGroupedByReferralId = latestReferrals.reduce((result, current) => {
                    result[current.id] = result[current.id] || [];
                    current.linkedOrders?.forEach(linkedOrder => {
                        if (linkedOrder.accessionNumber in ordersByAccession && !deletedLinkedOrders.has(linkedOrder.id)) {
                            result[current.id].push(ordersByAccession[linkedOrder.accessionNumber]);
                            delete ordersByAccession[linkedOrder.accessionNumber];
                        } else {
                            console.warn(
                                'Referral ' + current.id + ' is linked to order ' + linkedOrder.accessionNumber + 'but it is not loaded'
                            );
                        }
                    });
                    return result;
                }, Object.create(null));

                const imagingRequests: ImagingRequest[] = latestReferrals.map(
                    referral => new ImagingRequest(referral, ordersGroupedByReferralId[referral.id])
                );

                imagingRequests.push(
                    ...Object.values(ordersByAccession)
                        .flatMap(order => order)
                        .map(order => new ImagingRequest(null, [order]))
                );

                const cutoffTime = DateTime.now().minus({ hours: ImagingRequestsComponent.ImageRequestDisplayCutoffHours });
                return imagingRequests.filter(imagingRequest => imagingRequest.mostRecentCreatedAt >= cutoffTime);
            })
        );
    }

    getReferrals(selectedOrg: Observable<Org>, selectedSiteId: Observable<number>): Observable<GetReferral[]> {
        return combineLatest([
            timer(0, ImagingRequestsComponent.PeriodicRefreshInterval).pipe(takeUntil(this._destroy$)),
            selectedOrg,
            selectedSiteId,
            this.showReferralColumns$
        ]).pipe(
            switchMap(([_, org, selectedSiteId, showReferralColumns]) => {
                const now = DateTime.now();
                const referralCutoffTime = now.minus({ hours: ImagingRequestsComponent.RecentReferralsHoursRange });

                // Don't query for referrals if we aren't showing referral columns
                if (!showReferralColumns) {
                    return of([]);
                }

                return this.referralsService.getReferralList({
                    orgCode: org.orgCode,
                    siteIds: [selectedSiteId],
                    createdAtFrom: referralCutoffTime.toISO(),
                    createdAtTo: now.toISO()
                });
            }),
            shareReplay({ refCount: true })
        );
    }

    getOrders(selectedOrg: Observable<Org>, selectedSiteId: Observable<number>): Observable<IWorklistItemDto[]> {
        return combineLatest([
            timer(0, ImagingRequestsComponent.PeriodicRefreshInterval).pipe(takeUntil(this._destroy$)),
            selectedOrg,
            selectedSiteId
        ]).pipe(
            switchMap(([_, org, selectedSiteId]) => {
                const now = DateTime.now();
                const cutoffTime = now.minus({ hours: ImagingRequestsComponent.RecentOrdersHoursRange });
                return this.worklistService.query(org.orgCode, [selectedSiteId], cutoffTime, now, [], [], false, true);
            }),
            shareReplay({ refCount: true })
        );
    }

    canShowImagingRequest(imagingRequest: ImagingRequest, urgencies: Set<UrgencyType>, doctors: Set<string>, searchTerm: string): boolean {
        const urgencyMatches = urgencies.size === 0 || urgencies.has(imagingRequest.urgency);
        const doctorMatches = doctors.size === 0 || doctors.has(imagingRequest.referringDoctor);
        const sanitisedSearchTerm = searchTerm.trim().toLowerCase();
        const searchTermMatches =
            sanitisedSearchTerm.length === 0 ||
            imagingRequest.patientMrn.toLowerCase().includes(sanitisedSearchTerm) ||
            imagingRequest.patientFirstName?.toLowerCase()?.includes(sanitisedSearchTerm) ||
            imagingRequest.patientLastName?.toLowerCase()?.includes(sanitisedSearchTerm);
        return urgencyMatches && doctorMatches && searchTermMatches;
    }

    updateFilteredUrgencyTypes(newFilters: UrgencyType[]): void {
        this._filteredUrgencyTypes$.next(newFilters);
    }

    updateFilteredDoctors(newDoctors: string[]): void {
        this._filteredDoctors$.next(newDoctors);
    }

    submitSearch(event: UIEvent): void {
        event.preventDefault();
        const searchTermControl = this.searchForm.controls.searchTerm;
        if (searchTermControl.valid) {
            this._searchTerm$.next(searchTermControl.value);
        } else {
            const title = 'More characters are required';
            const message = 'Please enter three or more characters to search.';
            this._subscriptions.add(
                this.messageBoxService
                    .showMessageBox({
                        title,
                        message,
                        information: null,
                        icon: MessageBoxIcon.Information,
                        buttons: MessageBoxButtons.Ok
                    })
                    .subscribe()
            );
        }
    }

    openVettingRequestDialog() {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.maxWidth = '100vw';
        dialogConfig.width = '75vw';
        dialogConfig.maxHeight = '95vh';
        dialogConfig.restoreFocus = true;
        dialogConfig.disableClose = true;
        dialogConfig.panelClass = ['imaging-request-form-dialog', 'draggable-dialog-container', 'resize-dialog'];
        dialogConfig.backdropClass = 'general-dialog-backdrop';
        dialogConfig.enterAnimationDuration = 0;
        dialogConfig.exitAnimationDuration = 0;
        dialogConfig.autoFocus = false;

        this.dialog.open(ImagingRequestFormComponent, dialogConfig);
    }

    clearFiltersAndSearch(): void {
        this.clearSearch();
        this._filters.clearFilters();
    }

    clearSearch(): void {
        this.searchForm.controls.searchTerm.patchValue('');
        this._searchTerm$.next('');
    }

    onEntityUpsert(upsertNotification: UpsertNotification, orgCode: string, selectedSiteId: number) {
        if (upsertNotification.orgCode !== orgCode || selectedSiteId !== upsertNotification.siteId) {
            console.warn(
                'Received unexpected upsert notification for org/site not in selected org/site',
                upsertNotification.orgCode,
                orgCode,
                selectedSiteId,
                upsertNotification.siteId
            );
            return;
        }

        switch (upsertNotification.baseEntityType) {
            case EntityType.Referral:
                this.handleUpsertedReferral(upsertNotification.updatedEntityDetails as GetReferralDetails);
                break;
            case EntityType.Order:
                // TODO: Consider adding an orders entity event handler in CC API to subscribe to, rather than using the old hub.
                break;
            default:
                // Ignore
                break;
        }
    }

    onEntityDelete(entityId: string, entityType: EntityType) {
        switch (entityType) {
            case EntityType.Referral: {
                const referralId = Number(entityId);
                if (referralId) {
                    this.handleDeletedReferral(referralId);
                }
                break;
            }
            case EntityType.Order:
                // TODO: Consider adding an orders entity event handler in CC API to subscribe to, rather than using the old hub.
                break;
            case EntityType.LinkedOrder: {
                const linkedOrderId = Number(entityId);
                if (linkedOrderId) {
                    this.handleDeletedLinkedOrder(linkedOrderId);
                }
                break;
            }
            default:
                // Ignore
                break;
        }
    }

    handleUpsertedReferral(upsertedReferral: GetReferralDetails): void {
        const now = DateTime.now();
        const referralCutoffTime = now.minus({ hours: ImagingRequestsComponent.RecentReferralsHoursRange });
        if (DateTime.fromISO(upsertedReferral.createdAt) < referralCutoffTime) {
            return;
        }

        const upsertedReferralsMap = this._upsertedReferrals.value;
        let changed = false;
        const storedReferral = upsertedReferralsMap.get(upsertedReferral.id);
        if (storedReferral) {
            // Only update if it is not an older version, otherwise don't bother.
            if (storedReferral.updatedAt <= upsertedReferral.updatedAt) {
                upsertedReferralsMap.set(upsertedReferral.id, upsertedReferral);
                changed = true;
            }
        } else {
            upsertedReferralsMap.set(upsertedReferral.id, upsertedReferral);
            changed = true;
        }

        if (changed) {
            this._upsertedReferrals.next(upsertedReferralsMap);
        }
    }

    handleUpsertedOrder(upsertedOrder: IWorklistItemDto): void {
        const now = DateTime.now();
        const orderCutoffTime = now.minus({ hours: ImagingRequestsComponent.RecentOrdersHoursRange });
        if (DateTime.fromISO(upsertedOrder.studyDateTime) < orderCutoffTime) {
            return;
        }

        const upsertedOrdersMap = this._upsertedOrders.value;
        let changed = false;
        const storedOrder = upsertedOrdersMap.get(upsertedOrder.id);
        if (storedOrder) {
            // Only update if it is not an older version, otherwise don't bother.
            if (storedOrder.lastUpdatedAt <= upsertedOrder.lastUpdatedAt) {
                upsertedOrdersMap.set(upsertedOrder.id, upsertedOrder);
                changed = true;
            }
        } else {
            upsertedOrdersMap.set(upsertedOrder.id, upsertedOrder);
            changed = true;
        }

        if (changed) {
            this._upsertedOrders.next(upsertedOrdersMap);
        }
    }

    handleDeletedReferral(referralId: number) {
        const deletedReferrals = this._deletedReferrals.value;
        if (!deletedReferrals.has(referralId)) {
            deletedReferrals.add(referralId);
            this._deletedReferrals.next(deletedReferrals);
        }

        // Remove from upserts too, as it may have been updated then deleted
        const upsertedReferrals = this._upsertedReferrals.value;
        if (upsertedReferrals.has(referralId)) {
            upsertedReferrals.delete(referralId);
            this._upsertedReferrals.next(upsertedReferrals);
        }
    }

    handleDeletedLinkedOrder(id: number) {
        const deletedLinkedOrders = this._deletedLinkedOrderIds$.value;
        if (!deletedLinkedOrders.has(id)) {
            deletedLinkedOrders.add(id);
            this._deletedLinkedOrderIds$.next(deletedLinkedOrders);
        }
    }

    handleDeletedOrder(accessionNumber: string) {
        const deletedOrders = this._deletedOrders.value;
        if (!deletedOrders.has(accessionNumber)) {
            deletedOrders.add(accessionNumber);
            this._deletedOrders.next(deletedOrders);
        }

        // Remove from upserts too, as it may have been updated then deleted
        const upsertedOrders = this._upsertedOrders.value;
        if (upsertedOrders.has(accessionNumber)) {
            upsertedOrders.delete(accessionNumber);
            this._upsertedOrders.next(upsertedOrders);
        }
    }

    upsertChangedReferrals(referrals: GetReferral[]): Observable<(GetReferral | GetReferralDetails)[]> {
        return this._upsertedReferrals.pipe(
            map(upsertedReferrals => {
                const updatedReferrals: (GetReferral | GetReferralDetails)[] = [...referrals];
                upsertedReferrals.forEach(upsertedReferral => {
                    const indexOfMatchingReferral = updatedReferrals.findIndex(referral => referral.id === upsertedReferral.id);
                    if (indexOfMatchingReferral >= 0) {
                        updatedReferrals.splice(indexOfMatchingReferral, 1);
                    }
                    updatedReferrals.push(upsertedReferral);
                });
                return updatedReferrals;
            })
        );
    }

    upsertChangedOrders(orders: IWorklistItemDto[]): Observable<IWorklistItemDto[]> {
        return this._upsertedOrders.pipe(
            map(upsertedOrders => {
                const updatedOrders = [...orders];
                upsertedOrders.forEach(upsertedOrder => {
                    // this code does not handle singalR changes of linking / unlinking orders to referrals. It cannot unless signalR pipes through the linking information.
                    const indexOfMatchingOrder = updatedOrders.findIndex(order => order.accessionNumber === upsertedOrder.accessionNumber);
                    if (indexOfMatchingOrder >= 0) {
                        const linkedReferralInformation = updatedOrders[indexOfMatchingOrder].linkedReferralInformation;
                        upsertedOrder = { ...upsertedOrder, linkedReferralInformation };
                        updatedOrders.splice(indexOfMatchingOrder, 1);
                    }
                    updatedOrders.push(upsertedOrder);
                });
                return updatedOrders;
            })
        );
    }

    removeDeletedReferrals(referrals: GetReferral[]): Observable<GetReferral[]> {
        if (referrals.length > 0) {
            return this._deletedReferrals.pipe(map(deletedReferrals => referrals.filter(referral => !deletedReferrals.has(referral.id))));
        }

        return of([]);
    }

    removeDeletedOrders(orders: IWorklistItemDto[]): Observable<IWorklistItemDto[]> {
        if (orders.length > 0) {
            return this._deletedOrders.pipe(map(deletedOrders => orders.filter(order => !deletedOrders.has(order.accessionNumber))));
        }

        return of([]);
    }

    clearUpserts(): void {
        this._upsertedReferrals.next(new Map<number, GetReferralDetails>());
        this._deletedReferrals.next(new Set<number>());
        this._upsertedOrders.next(new Map<string, IWorklistItemDto>());
        this._deletedOrders.next(new Set<string>());
    }

    async initHubSubscriptions(orgCode: string, selectedSiteId: number): Promise<void> {
        try {
            if (this._referralHubSubscriptionId || this._hub) {
                await this.removeHubSubscription();
            }

            this._referralHubSubscriptionId = uuidv4();
            await this.referralsService.subscribeToReferralUpdates(
                this._referralHubSubscriptionId,
                orgCode,
                [selectedSiteId],
                upsertNotification => this.onEntityUpsert(upsertNotification, orgCode, selectedSiteId),
                (entityId, entityType) => this.onEntityDelete(entityId, entityType)
            );
            this._hub = await this.worklistService.startSignalRConnection(
                orgCode,
                [selectedSiteId],
                upsertNotification => this.handleUpsertedOrder(upsertNotification),
                accessionNumber => this.handleDeletedOrder(accessionNumber),
                accessionNumber => this.handleDeletedOrder(accessionNumber)
            );
        } catch (error) {
            console.warn(error);
        }
    }

    async removeHubSubscription(): Promise<void> {
        if (this._referralHubSubscriptionId) {
            await this.referralsService.unsubscribeFromReferralUpdates(this._referralHubSubscriptionId);
            this._referralHubSubscriptionId = null;
        }
        if (this._hub) {
            await this._hub.stop();
            this._hub = null;
        }
    }

    async ngOnDestroy(): Promise<void> {
        this._subscriptions.unsubscribe();
        if (this._hub) {
            await this._hub.stop();
        }
        this._destroy$.next(true);
    }

    shouldShowReferralColumns(org: Org): boolean {
        return org.countryCode === 'UK' || org.countryCode === 'IE';
    }
}
